import React, { FC, ReactElement, useEffect, useState } from "react";
import { Card, Typography, message, Row, Col } from "antd";
import { Link } from "react-router-dom";
import { BASE_URL } from "../../../constants/urlConstants";
import { RightOutlined } from "@ant-design/icons";
import "./PerfumeCardsSlider.css";

const { Text, Title } = Typography;

interface PerfumeResponse {
  id: number;
  perfumeTitle: string;
  brand: string;
  filename: string;
  price: number;
  discountPrice?: number;
  perfumeRating: number;
}

interface PerfumeCardsSliderProps {
  perfumes: { id: number }[];
}

const PerfumeCardsSlider: FC<PerfumeCardsSliderProps> = ({ perfumes }): ReactElement => {
  const [perfumeData, setPerfumeData] = useState<PerfumeResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!perfumes?.length) return;
    fetchPerfumes();
  }, [perfumes]);

  const fetchPerfumes = async () => {
    setLoading(true);
    try {
      const ids = perfumes.map((p) => p.id);
      const response = await fetch(`${BASE_URL}/api/v1/perfumes/ids`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(ids),
      });

      if (!response.ok) throw new Error("Failed to fetch perfumes");
      const data = await response.json();
      setPerfumeData(data);
    } catch (error) {
      console.error(error);
      message.error("Unable to load featured products");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flipkart-product-section">
      {/* Header Bar */}
      <div className="section-header">
        <Title level={4} className="section-title">
          Featured Products
        </Title>
        <RightOutlined className="section-arrow" />
      </div>

      {loading ? (
        <Text>Loading...</Text>
      ) : (
        <div className="flipkart-grid">
          {perfumeData.map((perfume) => (
            <Link
              key={perfume.id}
              to={`/product/${perfume.id}`}
              className="flipkart-product-link"
            >
              <Card
                hoverable
                className="flipkart-product-card"
                bodyStyle={{ padding: "8px" }}
                cover={
                  <div className="flipkart-image-container">
                    <img src={perfume.filename} alt={perfume.perfumeTitle} loading="lazy" />
                    <div className="perfume-rating-badge">
                      {perfume.perfumeRating.toFixed(1)} ★
                    </div>
                  </div>
                }
              >
                <Text className="product-title" ellipsis>
                  {perfume.perfumeTitle}
                </Text>
                <Text type="secondary" className="product-brand">
                  {perfume.brand}
                </Text>

                <div className="price-row">
                  {perfume.discountPrice && perfume.discountPrice < perfume.price ? (
                    <>
                      <Text delete className="old-price">
                        ₹{perfume.price}
                      </Text>
                      <Text strong className="new-price">
                        ₹{perfume.discountPrice}
                      </Text>
                    </>
                  ) : (
                    <Text strong className="new-price">
                      ₹{perfume.price}
                    </Text>
                  )}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default PerfumeCardsSlider;
